import React, { useCallback, useEffect, useMemo, useState } from "react";
import EdiText from "react-editext";
import styles from "./Section.module.scss";
import SectionModules from "./SectionModules";
import {
  useCreateContentMutation,
  ContentFragmentDoc,
  LessonType,
  useDeleteSectionMutation,
  useUpdateSectionMutation,
  Section as SectionSchema,
} from "@api/graphql-codegen";
import LocalPopup from "@components/local-popup/LocalPopup";
import Spinner from "@components/spinner/Spinner";
import PlusIcon from "src/assets/icons/plus_circle.svg?react";
import CloseIcon from "src/assets/icons/close.svg?react";
import IconPDF from "src/assets/icons/content_type_pdf.svg?react";
import IconAudio from "src/assets/icons/content_type_audio.svg?react";
import IconSlide from "src/assets/icons/content_type_slide.svg?react";
import IconVideo from "src/assets/icons/content_type_video.svg?react";
import IconForm from "src/assets/icons/content_type_feedback.svg?react";
import IconPencil from "src/assets/icons/pencil.svg?react";
import IconTrash from "src/assets/icons/trashbin.svg?react";
import SlideIcon from "src/assets/icons/content_type_slide_com.svg?react";
import { Content, ContentType, Lesson } from "src/types";
import { useEditUnit } from "../../edit/useEditUnit";
import { logger } from '../../../../../utils/logger';

type Props = {
  contents: Content[];
  lesson: Lesson;
  section: SectionSchema;
  lessonType: LessonType;
  lessonOpened: boolean;
  refetchUnit: () => void;
  sectionBeingDeleted: boolean;
};

function Section(props: Props) {
  const {
    contents = [],
    lesson,
    section,
    lessonType,
    lessonOpened,
    refetchUnit,
    sectionBeingDeleted,
  } = props;

  const { setContent, setSection, setLesson, setSelectionLevel } =
    useEditUnit();
  const [updateName, setUpdateName] = useState("");

  const [createNewContent, { loading }] = useCreateContentMutation({
    update(cache, res) {
      const data = res.data?.createContent;
      if (data) {
        const newContentRef = cache.writeFragment({
          data,
          fragment: ContentFragmentDoc,
        });
        cache.modify({
          id: `Section:${section?.id}`,
          fields: {
            modules(existingRefs = []) {
              return [...existingRefs, newContentRef];
            },
          },
        });
        setContent(data);
        const newSection = {
          ...section,
          modules: [...(section?.modules || []), { ...data }],
        };
        setSection(newSection);
        setLesson({
          ...lesson,
          sections: [...(lesson?.sections || []), { ...newSection }],
        });
        setSelectionLevel("SectionContent");
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
    },
  });
  const [removeSection, { loading: deleteLoading }] = useDeleteSectionMutation({
    onCompleted(res) {
      setLesson({
        ...lesson,
        sections: lesson.sections?.filter((s) => s.id !== section.id),
      });
      setSection(undefined);
    },
    update() {
      refetchUnit();
    },
    onError(e) {
      logger.debug(`[e]`, e);
    },
  });
  const [updateSection, { loading: updateLoading }] = useUpdateSectionMutation({
    onCompleted(res) {
      const data = res.updateSection;
      if (data) {
        const newSection = { ...section, name: updateName };
        setSection(newSection);
        setLesson({
          ...lesson,
          sections: [...(lesson?.sections || []), { ...newSection }],
        });
      }
    },
    update() {
      refetchUnit();
    },
    onError(e) {
      logger.debug(`[e]`, e);
    },
  });

  const [opened, setOpened] = useState(true);
  const [createPopupOpened, setCreatePopupOpened] = useState(false);

  const createContent = useCallback(
    (contentType: ContentType) => () => {
      const nextPosition =
        contents.reduce((acc: any, c) => {
          if (c.position! > acc) return c.position;
          return acc;
        }, 0) + 1;
      createNewContent({
        variables: {
          input: {
            lessonId: lesson.id,
            sectionId: section.id,
            type: contentType,
            lessonType: lessonType,
            position: nextPosition,
          },
        },
      });
      handleCloseCreatePopup();
    },
    [contents, createNewContent, lesson.id, lessonType, section.id]
  );

  useEffect(() => {
    if (lessonOpened) setOpened(true);
  }, [lessonOpened]);

  const wrapperClassName = useMemo(() => {
    const classes = [styles.wrapper];
    if (opened) {
      classes.push(styles.opened);
    }

    return classes.join(" ");
  }, [opened]);

  const handleShowCreatePopup = () => {
    setCreatePopupOpened(true);
  };

  const handleCloseCreatePopup = () => {
    setCreatePopupOpened(false);
  };

  const editSectionName = useCallback(
    (newName: string) => {
      if (!sectionBeingDeleted && !updateLoading) {
        setUpdateName(newName);
        updateSection({
          variables: {
            input: {
              id: section?.id,
              name: newName,
            },
          },
        });
      }
    },
    [section?.id, sectionBeingDeleted, updateLoading, updateSection]
  );

  const deleteSection = useCallback(() => {
    if (!sectionBeingDeleted && !deleteLoading) {
      removeSection({
        variables: {
          input: { id: section?.id },
        },
      });
    }
  }, [deleteLoading, removeSection, section?.id, sectionBeingDeleted]);

  const popupContent = useMemo(() => {
    return (
      <div className={styles.popup}>
        <div className={styles.popupHeader}>
          <div className={styles.popupTitle}>Add content</div>
          <div
            className={styles.popupCloseWrapper}
            onClick={handleCloseCreatePopup}
          >
            <CloseIcon />
          </div>
        </div>
        <div>
          <div className={styles.popupContent}>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.VIDEO)}
            >
              <IconVideo />
              <div className={styles.itemName}>Video</div>
            </div>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.AUDIO)}
            >
              <IconAudio />
              <div className={styles.itemName}>Audio</div>
            </div>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.SLIDE)}
            >
              <IconSlide />
              <div className={styles.itemName}>Slides</div>
            </div>
          </div>
          <div className={styles.popupContent}>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.PDF)}
            >
              <IconPDF />
              <div className={styles.itemName}>PDF</div>
            </div>
            <div
              className={styles.popupItemForm}
              onClick={createContent(ContentType.FORM)}
            >
              <IconForm />
              <div className={styles.itemName}>Form</div>
            </div>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.PARENT)}
            >
              <div>PS</div>
              <div className={styles.itemName}>Parent</div>
            </div>
          </div>
          <div className={styles.popupContent}>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.REVEALSLIDE)}
            >
              <SlideIcon />
              <div className={styles.itemName}>Slides.com</div>
            </div>
            <div
              className={styles.popupItem}
              onClick={createContent(ContentType.ZIPSLIDE)}
            >
              <SlideIcon />
              <div className={styles.itemName}>Zip Slides</div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [createContent]);

  return (
    <div className={wrapperClassName}>
      <section className={styles.sectionHeader}>
        <div className={styles.headerContent} title={section.name}>
          <EdiText
            type="text"
            containerProps={{ className: `${styles.sectionTitle}` }}
            value={section.name}
            onSave={editSectionName}
            editButtonContent={
              <IconPencil className={`${styles.sectionNameEdit}`} />
            }
            editButtonClassName={styles.editButton}
            validationMessage={"Section name can't be empty."}
            validation={(val) => !!val}
            viewContainerClassName={styles.sectionName}
          />
        </div>
        <div className={styles.durationWrapper}>
          {deleteLoading ? (
            <Spinner width={17} height={17} />
          ) : (
            <IconTrash
              className={styles.smallTrashIcon}
              onClick={deleteSection}
            />
          )}
        </div>
      </section>
      <div className={styles.content}>
        <SectionModules contents={contents} lesson={lesson} section={section} />
        <LocalPopup
          content={popupContent}
          open={createPopupOpened}
          onClose={handleCloseCreatePopup}
        >
          <div
            className={styles.addContentWrapper}
            onClick={handleShowCreatePopup}
          >
            <PlusIcon className={styles.addButtonIcon} />
            <div className={styles.addButtonTitle}>Add new module</div>
            {loading ? (
              <div className={styles.loading}>
                <Spinner width={20} height={20} />
              </div>
            ) : null}
          </div>
        </LocalPopup>
      </div>
    </div>
  );
}

export default React.memo(Section);
